import { Microfrontend } from '@cian/mf-react';
import * as React from 'react';

import { AdBannerPlaceholder } from './components/AdBannerPlaceholder';
import { useAdBannerCommonParameters, useAdBannerMicrofrontend, useAdBannerMicrofrontendEvents } from './hooks';

import type { TAdBannerMicrofrontendParameters, TAdBannerPlaceParameters } from './types';
import type { IMicrofrontendManifest } from '@cian/mf-registry/shared';

export interface IAdBannerMicrofrontendContainerProps {
  manifest: IMicrofrontendManifest;
  parameters: TAdBannerPlaceParameters;
  onStub?(): void;
  onError?(): void;
  onClose?(): void;
}

/**
 * Дженерик-контейнер микрофронтенда рекламных баннеров: ничего не знает о конкретном месте.
 * Резолвит общие параметры, держит размер плейсхолдером и монтирует микрофронтенд только на клиенте.
 * Какой шаблон рисовать, решает сам микрофронтенд по типу пришедшего баннера.
 */
export const AdBannerMicrofrontendContainer: React.FC<IAdBannerMicrofrontendContainerProps> = ({
  manifest,
  parameters,
  onStub,
  onError,
  onClose,
}) => {
  const { microfrontendRef, isReady } = useAdBannerMicrofrontend();
  const commonParameters = useAdBannerCommonParameters();

  useAdBannerMicrofrontendEvents({ placeId: commonParameters.placeId, isReady, onStub, onError, onClose });

  const { microfrontendName, imageVersion, runtimeName } = manifest;

  // Общие параметры — база, параметры места перекрывают их. Мемоизация держит стабильную
  // ссылку на parameters, чтобы микрофронтенд не перерисовывался на каждый рендер хоста.
  const mergedParameters = React.useMemo(
    (): TAdBannerMicrofrontendParameters => ({ ...commonParameters, ...parameters }),
    [parameters, commonParameters],
  );

  return (
    <AdBannerPlaceholder sizes={parameters.sizes} testId={`AdBannerMicrofrontend-${parameters.place}`}>
      {isReady && (
        <Microfrontend
          ref={microfrontendRef}
          name={microfrontendName}
          image={imageVersion}
          runtime={runtimeName}
          parameters={mergedParameters}
        />
      )}
    </AdBannerPlaceholder>
  );
};
